import React, { useEffect, useMemo, useState } from 'react';
import { useUserStats } from 'src/queries/loyalty';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { Column, DataTable, Pager, SearchField, SortDirection } from './DataTable';
import { Panel } from './Panel';
import { accent, bodyFont, customerName, formatCount, ink, surface } from './tokens';

interface UserStatsRow {
  id: number;
  first_name: string;
  last_name: string;
  total_purchases: number;
  total_rewards_issued: number;
  total_rewards_redeemed: number;
  current_total: number;
  current_streak: number;
}

const PAGE_SIZE = 10;

/** Rank tints for the top three. Positions below that get no badge at all. */
const RANK_TINT = ['#F4E3B2', '#E3E7EA', '#EEDCC8'];
const RANK_INK = ['#7A5A12', '#4C565E', '#7A4F25'];

/**
 * The leaderboard. Replaces the old podium-of-cards, which spent 200px of
 * height on three names and then repeated them in the table underneath.
 *
 * Lifetime figures straight from `loyalty/user-stats` — deliberately not period
 * filtered, because "who are my best customers" is not a question about a
 * window.
 */
export const TopCustomersTable: React.FC<{ merchantId: number }> = ({ merchantId }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState('total_purchases');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const {
    data: rows = [],
    isFetching: loading,
    isError,
    refetch,
  } = useUserStats(merchantId);

  const error = isError ? 'We could not load your customers.' : null;

  // A narrowing search should not leave the reader stranded on a page that no
  // longer exists.
  useEffect(() => {
    setPage(0);
  }, [search, sortKey, sortDirection]);

  const sorted = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? rows.filter((row) =>
          customerName(row.first_name, row.last_name).toLowerCase().includes(query)
        )
      : rows;

    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'customer') {
        return (
          direction *
          customerName(a.first_name, a.last_name).localeCompare(
            customerName(b.first_name, b.last_name)
          )
        );
      }
      const left = a[sortKey as keyof UserStatsRow] as number;
      const right = b[sortKey as keyof UserStatsRow] as number;
      return direction * ((left ?? 0) - (right ?? 0));
    });
  }, [rows, search, sortKey, sortDirection]);

  const pageRows = sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(key);
    // Names read naturally A–Z; counts read naturally highest first.
    setSortDirection(key === 'customer' ? 'asc' : 'desc');
  };

  /** Rank by the default ordering, so it stays meaningful while sorting. */
  const rankOf = (row: UserStatsRow) => {
    if (sortKey !== 'total_purchases' || sortDirection !== 'desc') return null;
    return sorted.indexOf(row) + 1;
  };

  const numeric = (value: number) => (
    <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.body }}>
      {formatCount(value ?? 0)}
    </Typography>
  );

  const columns: Column<UserStatsRow>[] = [
    {
      key: 'customer',
      label: 'Customer',
      sortable: true,
      width: 'minmax(0,2fr)',
      render: (row) => {
        const rank = rankOf(row);
        const medal = rank !== null && rank <= 3 ? rank - 1 : null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '8px',
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: medal !== null ? RANK_TINT[medal] : surface.grid,
                color: medal !== null ? RANK_INK[medal] : ink.faint,
                fontFamily: bodyFont,
                fontWeight: 600,
                fontSize: 12
              }}
            >
              {rank ?? '–'}
            </Box>
            <Typography
              noWrap
              sx={{ fontFamily: bodyFont, fontWeight: 500, fontSize: 14, color: ink.strong }}
            >
              {customerName(row.first_name, row.last_name)}
            </Typography>
          </Box>
        );
      }
    },
    {
      key: 'total_purchases',
      label: 'Stamps',
      numeric: true,
      sortable: true,
      width: 'minmax(70px,.7fr)',
      render: (row) => numeric(row.total_purchases)
    },
    {
      key: 'total_rewards_issued',
      label: 'Rewards',
      numeric: true,
      sortable: true,
      width: 'minmax(80px,.7fr)',
      render: (row) => numeric(row.total_rewards_issued)
    },
    {
      key: 'total_rewards_redeemed',
      label: 'Redeemed',
      numeric: true,
      sortable: true,
      width: 'minmax(90px,.7fr)',
      render: (row) => numeric(row.total_rewards_redeemed)
    },
    {
      key: 'current_total',
      label: 'On card',
      numeric: true,
      sortable: true,
      width: 'minmax(80px,.7fr)',
      render: (row) => numeric(row.current_total)
    },
    {
      key: 'current_streak',
      label: 'Streak',
      numeric: true,
      sortable: true,
      width: 'minmax(70px,.7fr)',
      render: (row) => (
        <Typography
          sx={{
            fontFamily: bodyFont,
            fontWeight: row.current_streak > 0 ? 600 : 400,
            fontSize: 14,
            color: row.current_streak > 0 ? accent.deep : ink.faint
          }}
        >
          {formatCount(row.current_streak ?? 0)}
        </Typography>
      )
    }
  ];

  return (
    <Panel
      title='Customers'
      subtitle={
        error ?? (loading ? 'Loading…' : `${formatCount(rows.length)} all time · ranked by stamps`)
      }
      hint='Lifetime totals per customer. “On card” is how many stamps they are holding toward their next reward.'
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SearchField value={search} onChange={setSearch} placeholder='Search by name' />
          <Tooltip title='Refresh'>
            <ButtonBase
              aria-label='Refresh customers'
              onClick={() => refetch()}
              sx={{
                width: 38,
                height: 38,
                borderRadius: '9px',
                border: `1px solid ${surface.borderStrong}`,
                color: ink.body,
                '&:hover': { backgroundColor: surface.grid }
              }}
            >
              <Refresh sx={{ fontSize: 18 }} />
            </ButtonBase>
          </Tooltip>
        </Box>
      }
    >
      <DataTable
        columns={columns}
        rows={pageRows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage={
          error
            ? 'Customer stats are unavailable right now. Try refreshing.'
            : search.trim()
              ? `No customers match “${search.trim()}”.`
              : 'No customers yet. They appear here after their first stamp.'
        }
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
      />
      {!loading && sorted.length > 0 && (
        <Pager
          page={page}
          pageSize={PAGE_SIZE}
          total={sorted.length}
          onPageChange={setPage}
          noun='customers'
        />
      )}
    </Panel>
  );
};

export default TopCustomersTable;
